import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import ScrollToTopButton from '../components/ScrollToTopButton';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CurrencySwitcher from '../components/CurrencySwitcher';
import PricingCards from '../components/PricingCards';
import { useAuth } from '../contexts/AuthContext';
import { testimonialsApi, publicApi } from '../lib/api';
import { 
    Sparkles, ArrowRight, Target, Users, BarChart3, Brain, 
    FileText, CheckCircle, Star, Zap, Shield, TrendingUp, Quote, Menu, X,
    ChevronLeft, ChevronRight
} from 'lucide-react';

export default function LandingPage() {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const isRecruiter = user?.role === 'recruiter';
    const canManageBilling = !user || ['admin', 'manager', 'billing_admin'].includes(user?.role);
    const [testimonials, setTestimonials] = useState(null);
    const [ratingStats, setRatingStats] = useState({ count: 0, average_rating: 0 });
    const [publicStats, setPublicStats] = useState({
        matching_accuracy: 0,
        active_jobs: 0,
        organizations: 0,
        analyzed_candidates: 0,
    });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const testimonialsScrollerRef = useRef(null);

    const scrollTestimonials = (direction) => {
        const node = testimonialsScrollerRef.current;
        if (!node) return;
        const card = node.querySelector('[data-testid^="testimonial-"]');
        const step = card ? card.offsetWidth + 32 : node.clientWidth * 0.9;
        node.scrollBy({ left: direction * step, behavior: 'smooth' });
    };
    useEffect(() => {
        testimonialsApi.list(20).then(res => {
            setTestimonials(res.data?.testimonials || []);
        }).catch(() => {
            setTestimonials([]);
        });
        testimonialsApi.stats().then(res => {
            setRatingStats({
                count: res.data?.count || 0,
                average_rating: res.data?.average_rating || 0,
            });
        }).catch(() => {
            // silent — badge will simply not render
        });
        publicApi.stats().then(res => {
            setPublicStats({
                matching_accuracy: res.data?.matching_accuracy || 0,
                active_jobs: res.data?.active_jobs || 0,
                organizations: res.data?.organizations || 0,
                analyzed_candidates: res.data?.analyzed_candidates || 0,
            });
        }).catch(() => {
            // silent — fallback to zeros already set
        });
    }, []);

    const features = [
        {
            icon: <Brain className="w-6 h-6" />,
            title: t('landing.feat_parsing_title'),
            description: t('landing.feat_parsing_desc')
        },
        {
            icon: <Target className="w-6 h-6" />,
            title: t('landing.feat_matching_title'),
            description: t('landing.feat_matching_desc')
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: t('landing.feat_dashboard_title'),
            description: t('landing.feat_dashboard_desc')
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: t('landing.feat_multitenant_title'),
            description: t('landing.feat_multitenant_desc')
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: t('landing.feat_fast_title'),
            description: t('landing.feat_fast_desc')
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: t('landing.feat_rgpd_title'),
            description: t('landing.feat_rgpd_desc')
        }
    ];

    const formatCount = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 10_000) return `${Math.round(n / 1000)}K`;
        if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
        return `${n}`;
    };

    const stats = [
        { value: "95%", label: t('landing.stat_accuracy') },
        { value: "3x", label: t('landing.stat_faster') },
        { value: formatCount(publicStats.active_jobs), label: t('landing.stat_active_jobs') },
        { value: formatCount(publicStats.analyzed_candidates), label: t('landing.stat_cvs_analyzed') }
    ];

    return (
        <div className="min-h-screen bg-white" data-testid="landing-page">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-black/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <BrandLogo size={36} />
                        <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">GUIRETAL</span>
                    </Link>
                    
                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-[#4B5563] hover:text-[#0A0A0A] transition-colors">{t('nav.features')}</a>
                        <a href="#pricing" className="text-[#4B5563] hover:text-[#0A0A0A] transition-colors">{t('nav.pricing')}</a>
                        <a href="#testimonials" className="text-[#4B5563] hover:text-[#0A0A0A] transition-colors">{t('nav.testimonials')}</a>
                    </div>
                    
                    {/* Desktop CTAs */}
                    <div className="hidden md:flex items-center gap-3">
                        <LanguageSwitcher />
                        <CurrencySwitcher />
                        <Link to="/login">
                            <Button variant="ghost" className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="nav-login-btn">
                                {t('common.login')}
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="nav-register-btn">
                                {t('common.register')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile controls */}
                    <div className="md:hidden flex items-center gap-2">
                        <CurrencySwitcher />
                        <LanguageSwitcher />
                        <button
                            onClick={() => setMobileMenuOpen(v => !v)}
                            className="p-2 rounded-md hover:bg-[#F3F4F6] text-[#0A0A0A]"
                            aria-label="Menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile dropdown menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-nav-menu">
                        <div className="px-4 py-4 flex flex-col gap-1">
                            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-[#4B5563] hover:bg-[#F3F4F6] rounded-md">{t('nav.features')}</a>
                            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-[#4B5563] hover:bg-[#F3F4F6] rounded-md">{t('nav.pricing')}</a>
                            <a href="#testimonials" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 text-[#4B5563] hover:bg-[#F3F4F6] rounded-md">{t('nav.testimonials')}</a>
                            <div className="h-px bg-[#E5E7EB] my-2" />
                            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                                <Button variant="ghost" className="w-full justify-start text-[#4B5563]" data-testid="mobile-nav-login">
                                    {t('common.login')}
                                </Button>
                            </Link>
                            <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                                <Button className="w-full bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="mobile-nav-register">
                                    {t('common.register')}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#F9FAFB] to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 animate-fade-in">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight leading-tight">
                                {t('landing.hero_title_1')}<br />
                                <span className="text-[#002FA7]">{t('landing.hero_title_2')}</span><br />
                                {t('landing.hero_title_3')}
                            </h1>
                            
                            <p className="text-lg text-[#4B5563] max-w-xl">
                                {t('landing.hero_description')}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/register">
                                    <Button size="lg" className="bg-[#002FA7] hover:bg-[#002480] text-white h-12 px-8" data-testid="hero-cta-btn">
                                        {t('landing.cta_start_free')}
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                            
                            {ratingStats.count > 0 && (
                                <div className="flex items-center gap-6 pt-4" data-testid="hero-rating-badge">
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#002FA7] to-[#3B82F6] border-2 border-white" />
                                        ))}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1" data-testid="hero-rating-stars">
                                            {[1, 2, 3, 4, 5].map((i) => {
                                                const filled = i <= Math.floor(ratingStats.average_rating);
                                                const halfFilled = !filled && i - 0.5 <= ratingStats.average_rating;
                                                return (
                                                    <Star
                                                        key={i}
                                                        className={`w-4 h-4 ${
                                                            filled
                                                                ? 'fill-[#FFCC00] text-[#FFCC00]'
                                                                : halfFilled
                                                                    ? 'fill-[#FFCC00]/50 text-[#FFCC00]'
                                                                    : 'text-[#E5E7EB]'
                                                        }`}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <p className="text-sm text-[#4B5563]" data-testid="hero-rating-text">
                                            {t('landing.rating_text', { rating: ratingStats.average_rating.toFixed(1), count: ratingStats.count })}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="relative animate-fade-in stagger-2">
                            <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/10 to-[#3B82F6]/10 rounded-2xl blur-3xl" />
                            <img
                                src={(i18n.language || '').startsWith('en')
                                    ? "https://customer-assets-7cd3h4nn.emergentagent.net/job_smart-recruit-pro-1/artifacts/353c0xd3_talent-funnel-en.jpg"
                                    : "https://customer-assets-7cd3h4nn.emergentagent.net/job_smart-recruit-pro-1/artifacts/jdhvvvnm_talent-funnel.jpg"}
                                alt={t('landing.hero_image_alt')}
                                className="relative rounded-2xl shadow-2xl border border-[#E5E7EB] object-contain w-full h-[480px] bg-[#F5F7FA]"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-6 bg-[#002FA7]">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-4xl font-bold text-white font-['Outfit']">{stat.value}</div>
                                <div className="text-white/70 mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] font-['Outfit']">
                            {t('landing.features_title_long')}
                        </h2>
                        <p className="text-[#4B5563] mt-4 max-w-2xl mx-auto">
                            {t('landing.features_subtitle_long')}
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div 
                                key={index} 
                                className="p-6 border border-[#E5E7EB] rounded-lg hover:border-[#002FA7]/30 hover:shadow-lg transition-all duration-200 card-hover"
                            >
                                <div className="w-12 h-12 bg-[#DBEAFE] rounded-lg flex items-center justify-center text-[#002FA7] mb-4">
                                    {feature.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{feature.title}</h3>
                                <p className="text-[#4B5563] mt-2">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-24 px-6 bg-[#F9FAFB]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] font-['Outfit']">
                            {t('landing.steps_title')}
                        </h2>
                        <p className="text-[#4B5563] mt-4">{t('landing.steps_subtitle')}</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { step: "1", title: t('landing.step1_title'), description: t('landing.step1_desc') },
                            { step: "2", title: t('landing.step2_title'), description: t('landing.step2_desc') },
                            { step: "3", title: t('landing.step3_title'), description: t('landing.step3_desc') },
                            { step: "4", title: t('landing.step4_title'), description: t('landing.step4_desc') }
                        ].map((item, index) => (
                            <div key={index} className="relative">
                                <div className="text-7xl font-bold text-[#002FA7]/10 font-['Outfit'] absolute -top-4 -left-2">
                                    {item.step}
                                </div>
                                <div className="relative pt-8 pl-4">
                                    <h3 className="text-xl font-semibold text-[#0A0A0A] font-['Outfit']">{item.title}</h3>
                                    <p className="text-[#4B5563] mt-2">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] font-['Outfit']">
                            {t('landing.testimonials_title')}
                        </h2>
                        <p className="text-[#4B5563] mt-4">{t('landing.testimonials_subtitle')}</p>
                    </div>

                    {testimonials === null ? (
                        <div className="text-center text-[#9CA3AF] text-sm">{t('common.loading')}</div>
                    ) : testimonials.length === 0 ? (
                        <div
                            className="max-w-xl mx-auto text-center py-12 px-8 border border-dashed border-[#E5E7EB] rounded-lg bg-[#F9FAFB]"
                            data-testid="testimonials-empty"
                        >
                            <Quote className="w-10 h-10 text-[#9CA3AF] mx-auto mb-4" />
                            <p className="text-[#4B5563] text-lg">{t('landing.testimonials_empty')}</p>
                            <p className="text-[#9CA3AF] text-sm mt-2">
                                {t('landing.testimonials_empty_sub')}
                            </p>
                        </div>
                    ) : (
                        <div className="relative">
                            {testimonials.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        aria-label={t('landing.testimonials_prev', { defaultValue: 'Précédent' })}
                                        onClick={() => scrollTestimonials(-1)}
                                        data-testid="testimonials-scroll-prev"
                                        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border border-[#E5E7EB] shadow-md hover:shadow-lg hover:border-[#002FA7]/40 transition-all"
                                    >
                                        <ChevronLeft className="w-5 h-5 text-[#0A0A0A]" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={t('landing.testimonials_next', { defaultValue: 'Suivant' })}
                                        onClick={() => scrollTestimonials(1)}
                                        data-testid="testimonials-scroll-next"
                                        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border border-[#E5E7EB] shadow-md hover:shadow-lg hover:border-[#002FA7]/40 transition-all"
                                    >
                                        <ChevronRight className="w-5 h-5 text-[#0A0A0A]" />
                                    </button>
                                </>
                            )}
                            <div
                                ref={testimonialsScrollerRef}
                                data-testid="testimonials-scroller"
                                className="flex gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 scrollbar-thin"
                                style={{ scrollbarWidth: 'thin' }}
                            >
                                {testimonials.map((item) => (
                                    <div
                                        key={item.id}
                                        className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)] p-6 border border-[#E5E7EB] rounded-lg bg-white flex flex-col"
                                        data-testid={`testimonial-${item.id}`}
                                    >
                                        <Quote className="w-8 h-8 text-[#002FA7]/20 mb-4" />
                                        <p className="text-[#0A0A0A] mb-6 flex-1">&ldquo;{item.content}&rdquo;</p>
                                        {item.rating ? (
                                            <div className="flex items-center gap-1 mb-3">
                                                {[1, 2, 3, 4, 5].map((i) => (
                                                    <Star
                                                        key={i}
                                                        className={`w-4 h-4 ${i <= item.rating ? 'fill-[#FFCC00] text-[#FFCC00]' : 'text-[#E5E7EB]'}`}
                                                    />
                                                ))}
                                            </div>
                                        ) : null}
                                        <div className="text-sm">
                                            <div className="font-semibold text-[#0A0A0A]">{item.author_name}</div>
                                            <div className="text-[#9CA3AF]">
                                                {item.author_title}
                                                {item.author_company ? ` · ${item.author_company}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-24 px-4 sm:px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] font-['Outfit']">
                            {t('landing.pricing_title')}
                        </h2>
                        <p className="text-[#4B5563] mt-4">{t('landing.pricing_subtitle')}</p>
                    </div>

                    <PricingCards variant="landing" canManageBilling={canManageBilling} />

                    {isRecruiter && (
                        <p
                            data-testid="landing-recruiter-notice"
                            className="mt-8 max-w-2xl mx-auto text-center text-sm text-[#6B7280] italic"
                        >
                            {t('pricing.recruiter_notice')}
                        </p>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 bg-[#002FA7]">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">
                        {t('landing.cta_section_title')}
                    </h2>
                    <p className="text-white/80 mt-4 text-lg">
                        {t('landing.cta_section_subtitle')}
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register">
                            <Button size="lg" className="bg-white text-[#002FA7] hover:bg-[#F9FAFB] h-12 px-8">
                                {t('landing.cta_start_free')}
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <BrandLogo size={36} />
                                <span className="text-xl font-bold text-white font-['Outfit']">GUIRETAL</span>
                            </div>
                            <p className="text-[#9CA3AF] text-sm">
                                {t('landing.footer_tagline')}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold mb-4">{t('landing.footer_col_product')}</h4>
                            <ul className="space-y-2 text-[#9CA3AF]">
                                <li><a href="#features" className="hover:text-white transition-colors">{t('landing.footer_features')}</a></li>
                                <li><a href="#pricing" className="hover:text-white transition-colors">{t('landing.footer_pricing')}</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer_api')}</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold mb-4">{t('landing.footer_col_company')}</h4>
                            <ul className="space-y-2 text-[#9CA3AF]">
                                <li><Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about-link">{t('landing.footer_about')}</Link></li>
                                {/* Blog & Careers links are intentionally hidden until we have real
                                    content to host. Kept as commented-out placeholders so they can be
                                    re-enabled trivially in the future.
                                    <li><Link to="/blog" className="hover:text-white transition-colors">{t('landing.footer_blog')}</Link></li>
                                    <li><Link to="/careers" className="hover:text-white transition-colors">{t('landing.footer_careers')}</Link></li>
                                */}
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold mb-4">{t('landing.footer_col_legal')}</h4>
                            <ul className="space-y-2 text-[#9CA3AF]">
                                <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-cgu-link">{t('landing.footer_terms')}</Link></li>
                                <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">{t('landing.footer_privacy')}</Link></li>
                                <li><Link to="/gdpr" className="hover:text-white transition-colors" data-testid="footer-rgpd-link">{t('landing.footer_rgpd')}</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-white/10 mt-12 pt-8 text-center text-[#9CA3AF] text-sm" data-testid="landing-footer-copyright">
                        {t('landing.footer_copyright')}
                    </div>
                </div>
            </footer>

            <ScrollToTopButton withScrollDown />
        </div>
    );
}
